import { useRef, useEffect } from 'react'
import { motion, useInView } from 'framer-motion'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { UP, SPRING } from '@/lib/motion'

gsap.registerPlugin(ScrollTrigger)

const STEPS = [
  {
    n: '01',
    title: 'Call · ይደውሉ',
    desc: 'Call +251 99 774 5317. Tell us your space and what needs cleaning.'
  },
  {
    n: '02',
    title: 'Schedule · ቀጠሮ',
    desc: 'Pick your time. Same-day service available for urgent needs.'
  },
  {
    n: '03',
    title: 'Clean · ፅዳቱ',
    desc: 'Team arrives with professional equipment. Sit back and relax.'
  },
  {
    n: '04',
    title: 'Shine · ብርሃን',
    desc: 'Spotless. Guaranteed or we return free. Your satisfaction matters.'
  }
]

export function Process() {
  const sectionRef = useRef<HTMLElement>(null)
  const pathRef = useRef<SVGPathElement>(null)
  const isInView = useInView(sectionRef, { once: true, margin: '-80px' })

  useEffect(() => {
    if (!pathRef.current || !sectionRef.current) return

    const path = pathRef.current
    const length = path.getTotalLength()

    gsap.set(path, {
      strokeDasharray: length,
      strokeDashoffset: length
    })

    const trigger = ScrollTrigger.create({
      trigger: sectionRef.current,
      start: 'top center',
      end: 'bottom center',
      scrub: 1,
      onUpdate: (self) => {
        gsap.to(path, {
          strokeDashoffset: length * (1 - self.progress),
          duration: 0.1
        })
      }
    })

    return () => {
      trigger.kill()
      gsap.killTweensOf(path)
    }
  }, [])

  return (
    <section
      ref={sectionRef}
      style={{
        background: 'var(--elevated)',
        padding: 'var(--pad-section) max(5vw, 24px)'
      }}
    >
      <div style={{ maxWidth: 1400, margin: '0 auto' }}>
        {/* Section header */}
        <motion.div {...UP} style={{ marginBottom: 64, textAlign: 'center' }}>
          <span
            style={{
              fontFamily: 'DM Sans',
              fontSize: '0.8rem',
              color: 'var(--lime)',
              letterSpacing: '0.15em',
              textTransform: 'uppercase'
            }}
          >
            How It Works · እንዴት እንደምንሰራ
          </span>
          <h2
            style={{
              fontFamily: 'Bebas Neue',
              fontSize: 'var(--h2)',
              color: 'var(--white)',
              marginTop: 12,
              lineHeight: 1
            }}
          >
            Simple 4-Step Process
          </h2>
        </motion.div>

        {/* Process timeline */}
        <div style={{ position: 'relative' }}>
          {/* SVG Path */}
          <svg
            style={{
              position: 'absolute',
              top: 60,
              left: 0,
              right: 0,
              height: 4,
              width: '100%',
              zIndex: 0
            }}
            preserveAspectRatio="none"
            viewBox="0 0 1000 4"
          >
            <path
              ref={pathRef}
              d="M 0 2 L 1000 2"
              stroke="var(--lime)"
              strokeWidth="2"
              fill="none"
            />
          </svg>

          {/* Steps */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: 24,
              position: 'relative',
              zIndex: 1
            }}
          >
            {STEPS.map((step, i) => (
              <motion.div
                key={step.n}
                initial={{ opacity: 0, y: 40 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: i * 0.15, ...SPRING }}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center'
                }}
              >
                {/* Node */}
                <motion.div
                  style={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    border: '2px solid var(--lime)',
                    background: 'var(--elevated)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: 24,
                    fontFamily: 'Bebas Neue',
                    fontSize: '1.2rem',
                    color: 'var(--lime)'
                  }}
                  whileInView={{
                    background: 'var(--lime)',
                    color: 'var(--black)',
                    boxShadow: '0 0 30px rgba(232,255,0,0.5)'
                  }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 + i * 0.2 }}
                >
                  {step.n}
                </motion.div>

                {/* Title */}
                <h3
                  style={{
                    fontFamily: 'DM Sans',
                    fontWeight: 700,
                    fontSize: '1.1rem',
                    color: 'var(--white)',
                    marginBottom: 8
                  }}
                >
                  {step.title}
                </h3>

                {/* Description */}
                <p
                  style={{
                    fontFamily: 'DM Sans',
                    fontSize: '0.9rem',
                    color: 'var(--white-60)',
                    lineHeight: 1.6,
                    maxWidth: 200
                  }}
                >
                  {step.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
